import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { all_routes } from '../../router/all_routes';
import {
    getLeaveRequestById,
    getEmpLeaveFileAttachment,
    getLeaveDropDown,
} from '../../../core/data/redux/leaveSlice';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import {
    viewLeaveAttachmentFile,
    parseEmpLeaveAttachmentList,
    normalizeLeaveAttachmentForUi,
    LEAVE_ADDITIONAL_FIELDS_HIDDEN_KEYS,
    KNOWN_LEAVE_FIELD_LABELS as KNOWN_FIELD_LABELS,
} from './leaveUtils';

interface LeaveManagementDetailsProps { }

const LeaveManagementDetails = (props: LeaveManagementDetailsProps) => {
    const routes = all_routes;
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();
    const behalf = (location.state as any) || {};

    const { leaveRequestById, leaveDropDown } = useSelector((state: any) => state.leavesState || {});
    const employeesState = useSelector((state: any) => state.employeesState || {});
    const loggedInEmpId = employeesState?.data?.data?.empId || '';

    const [attachments, setAttachments] = useState<any[]>([]);
    const [loadingAttachments, setLoadingAttachments] = useState(false);

    useEffect(() => {
        if (!loggedInEmpId) {
            dispatch(getMyProfile({}) as any);
        }
        dispatch(getLeaveDropDown({}) as any);
    }, [dispatch, loggedInEmpId]);

    useEffect(() => {
        if (id) {
            dispatch(getLeaveRequestById({ requestID: id }));
        }
    }, [dispatch, id]);

    const rawData = useMemo(() => {
        const payload = leaveRequestById;
        const nodes = [payload, payload?.data, payload?.data?.data, payload?.data?.data?.data];
        for (const node of nodes) {
            if (node && typeof node === "object" && !Array.isArray(node) && (node.RequestID || node.EmployeeID || node.AbsenceTypeName)) {
                return node;
            }
        }
        return payload?.data || {};
    }, [leaveRequestById]);

    useEffect(() => {
        const rawEmp = behalf.overrideEmployeeID || rawData.EmployeeID || rawData.employee_details?.empId || loggedInEmpId;
        const empId = String(rawEmp || '').trim().padStart(8, '0');
        const leaveReqId = String(rawData.RequestID || id || '').trim();

        if (empId && leaveReqId && !/^0+$/.test(empId)) {
            setLoadingAttachments(true);
            dispatch(getEmpLeaveFileAttachment({ empId, leaveRequestId: leaveReqId }))
                .unwrap()
                .then((res: any) => {
                    const rawList = parseEmpLeaveAttachmentList(res);
                    const normalized = rawList.map(normalizeLeaveAttachmentForUi);
                    setAttachments(normalized);
                })
                .catch(() => setAttachments([]))
                .finally(() => setLoadingAttachments(false));
        }
    }, [dispatch, behalf.overrideEmployeeID, rawData.EmployeeID, rawData.employee_details?.empId, rawData.RequestID, id, loggedInEmpId]);

    // Parse dropdown lists for mapping codes -> friendly labels
    const dropdownBuckets = useMemo(() => {
        const rawList: any[] = Array.isArray(leaveDropDown?.data)
            ? leaveDropDown.data
            : Array.isArray(leaveDropDown?.data?.data)
            ? leaveDropDown.data.data
            : [];
        return {
            customer02: rawList.filter((i) => i.dd === 'Customer02'),
            customer09: rawList.filter((i) => i.dd === 'Customer09'),
            customer10: rawList.filter((i) => i.dd === 'Customer10'),
        };
    }, [leaveDropDown]);

    const extractApplicantNote = (noteStr: string) => {
        if (!noteStr) return '';
        if (noteStr.includes('::::')) {
            const parts = noteStr.split('::::');
            return parts[2] || parts[1] || noteStr;
        }
        return noteStr;
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString || dateString === 'N/A' || dateString === '-') return "N/A";
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('-');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
        }
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch (e) {
            return dateString;
        }
    };

    // Compile all Additional Fields from AdditionalFields object + direct properties
    const additionalFieldsList = useMemo(() => {
        const merged: Record<string, any> = {
            ...(rawData.AdditionalFields || {}),
        };

        // Fallbacks if not in AdditionalFields dictionary
        if (!merged.CUSTOMER01 && rawData.CUSTOMER01) merged.CUSTOMER01 = rawData.CUSTOMER01;
        if (!merged.CUSTOMER02 && rawData.CUSTOMER02) merged.CUSTOMER02 = rawData.CUSTOMER02;
        if (!merged.CUSTOMER03 && rawData.CUSTOMER03) merged.CUSTOMER03 = rawData.CUSTOMER03;
        if (!merged.CUSTOMER05 && rawData.CUSTOMER05) merged.CUSTOMER05 = rawData.CUSTOMER05;
        if (!merged.CUSTOMER06 && rawData.CUSTOMER06) merged.CUSTOMER06 = rawData.CUSTOMER06;
        if (!merged.CUSTOMER07 && rawData.CUSTOMER07) merged.CUSTOMER07 = rawData.CUSTOMER07;
        if (!merged.CUSTOMER08 && rawData.CUSTOMER08) merged.CUSTOMER08 = rawData.CUSTOMER08;
        if (!merged.CUSTOMER09 && rawData.CUSTOMER09) merged.CUSTOMER09 = rawData.CUSTOMER09;
        if (!merged.CUSTOMER10 && rawData.CUSTOMER10) merged.CUSTOMER10 = rawData.CUSTOMER10;
        if (!merged.RMDDA && rawData.RMDDA) merged.RMDDA = rawData.RMDDA;

        return Object.entries(merged)
            .filter(([key, value]) => {
                if (LEAVE_ADDITIONAL_FIELDS_HIDDEN_KEYS.includes(key)) return false;
                if (value === null || value === undefined || value === '') return false;
                return true;
            })
            .map(([key, value]) => {
                let displayVal = String(value);

                if (key === 'CUSTOMER08') {
                    displayVal = value === true || value === 'true' || value === 'X' ? 'Yes' : 'No';
                } else if (key === 'CUSTOMER02') {
                    const match = dropdownBuckets.customer02.find((i) => i.key === String(value));
                    displayVal = match ? match.value : String(value);
                } else if (key === 'CUSTOMER09') {
                    const match = dropdownBuckets.customer09.find((i) => i.key === String(value));
                    displayVal = match ? match.value : String(value);
                } else if (key === 'CUSTOMER10') {
                    const match = dropdownBuckets.customer10.find((i) => i.key === String(value));
                    displayVal = match ? match.value : String(value);
                } else if (key === 'RMDDA') {
                    displayVal = formatDate(String(value));
                } else if (key === 'ABRST') {
                    const hoursSource =
                        rawData.WorkingHoursDuration !== undefined &&
                        rawData.WorkingHoursDuration !== null &&
                        String(rawData.WorkingHoursDuration).trim() !== ''
                            ? String(rawData.WorkingHoursDuration).trim()
                            : String(value ?? '').trim();
                    const parsed = parseFloat(hoursSource);
                    displayVal = !isNaN(parsed) ? `${parsed.toFixed(2)} Hrs` : String(value);
                }

                return {
                    key,
                    label: KNOWN_FIELD_LABELS[key] || key,
                    value: displayVal,
                };
            });
    }, [rawData, dropdownBuckets]);

    // Remarks list matching mobile details.remarks
    const remarksList = Array.isArray(rawData.remarks) ? rawData.remarks : [];
    const applicantNote = extractApplicantNote(rawData.Notes);

    const fullRequestId = String(rawData.RequestID || id || '-');

    // In mobile, WorkingHoursDuration (e.g. 16.00 hrs) takes precedence over total raw ABRST (e.g. 24.00)
    const rawHoursStr = String(
        rawData.WorkingHoursDuration !== undefined && rawData.WorkingHoursDuration !== null && String(rawData.WorkingHoursDuration).trim() !== ''
            ? rawData.WorkingHoursDuration
            : rawData.ABRST || rawData.AdditionalFields?.ABRST || '0.00'
    ).trim();
    const parsedHours = parseFloat(rawHoursStr);
    const absenceHoursFormatted = !isNaN(parsedHours) ? parsedHours.toFixed(2) : '0.00';
    const creationDate = rawData.FirstSubmissionDate || rawData.created_at || rawData.FirstSubmissionDate1 || '-';

    const leaveData = {
        typeOfLeave: rawData.AbsenceTypeName || rawData.typeOfLeave || 'N/A',
        status: rawData.StatusName || rawData.StatusCode || 'Sent',
        requestID: fullRequestId,
        generalData: {
            startDate: rawData.StartDate || 'N/A',
            endDate: rawData.EndDate || 'N/A',
            creationDate: creationDate,
            absenceHours: absenceHoursFormatted,
            durationDays: rawData.WorkingDaysDuration || '1.0',
            approver: rawData.ApproverName || rawData.ApproverUserID || 'N/A',
            approverId: rawData.ApproverUserID || rawData.ApproverPernr || '',
        },
        attachedFiles: attachments.length > 0
            ? attachments
            : Array.isArray(rawData.Attachments) && rawData.Attachments.length > 0
            ? rawData.Attachments.map(normalizeLeaveAttachmentForUi)
            : Array.isArray(rawData.AttachmentDetails) && rawData.AttachmentDetails.length > 0
            ? rawData.AttachmentDetails.map(normalizeLeaveAttachmentForUi)
            : []
    };

    const getFileIcon = (fileType: string) => {
        switch (fileType.toLowerCase()) {
            case 'pdf':
                return 'ti ti-file-type-pdf text-danger';
            case 'jpg':
            case 'jpeg':
            case 'png':
                return 'ti ti-file-type-jpg text-primary';
            case 'doc':
            case 'docx':
                return 'ti ti-file-type-doc text-info';
            default:
                return 'ti ti-file text-secondary';
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
                    <div className="my-auto mb-2">
                        <div className="d-flex align-items-center">
                            <Link
                                to={routes.LeaveManagement.list}
                                state={behalf.overrideEmployeeID ? behalf : undefined}
                                className="text-primary text-decoration-none me-3"
                            >
                                <i className="ti ti-arrow-left fs-24"></i>
                            </Link>
                            <h3 className="page-title mb-0">Leave Request Details</h3>
                        </div>
                    </div>
                </div>

                {/* Header Information Pane */}
                <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden">
                    <div className="card-body p-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex flex-column">
                            <h4 className="mb-1 text-dark fw-bold text-break" title={leaveData.requestID}>
                                Request #{leaveData.requestID}
                            </h4>
                            <p className="text-muted mb-0 fs-14">View complete details of your leave application below</p>
                        </div>
                        <span className="badge bg-success fs-6 px-3 py-2 rounded-pill shadow-sm">
                            {leaveData.status}
                        </span>
                    </div>
                </div>

                <div className="row g-4 mb-4">
                    {/* General Information */}
                    <div className="col-12 col-xl-6">
                        <div className="card shadow-sm border-0 h-100 rounded-3">
                            <div className="card-header bg-white border-bottom pt-4 pb-3 px-4">
                                <h5 className="card-title text-dark fw-bold mb-0">General Details</h5>
                            </div>
                            <div className="card-body px-4 pb-4 pt-4">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="p-3 bg-light rounded shadow-sm border-start border-4 border-primary">
                                            <p className="text-muted mb-1 fs-12 fw-semibold text-uppercase">Reference / Request ID</p>
                                            <h6 className="mb-0 text-dark fw-bold text-break" style={{ wordBreak: 'break-all', fontSize: '13px' }}>
                                                {leaveData.requestID}
                                            </h6>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="p-3 bg-light rounded shadow-sm border-start border-4 h-100" style={{ borderColor: '#ab1f24' }}>
                                            <p className="text-muted mb-1 fs-12 fw-semibold text-uppercase">Leave Type</p>
                                            <h6 className="mb-0 fw-bold">{leaveData.typeOfLeave}</h6>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="p-3 bg-light rounded shadow-sm border-start border-4 border-dark h-100">
                                            <p className="text-muted mb-1 fs-12 fw-semibold text-uppercase">Duration</p>
                                            <h6 className="mb-0 fw-bold">
                                                {parseFloat(leaveData.generalData.durationDays) > 0 ? `${leaveData.generalData.durationDays} Days` : ''} 
                                                {parseFloat(leaveData.generalData.absenceHours) > 0 ? ` (${leaveData.generalData.absenceHours} Hrs)` : ''}
                                            </h6>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="p-3 bg-light rounded shadow-sm">
                                            <p className="text-muted mb-1 fs-12">Start Date</p>
                                            <h6 className="mb-0">{formatDate(leaveData.generalData.startDate)}</h6>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="p-3 bg-light rounded shadow-sm">
                                            <p className="text-muted mb-1 fs-12">End Date</p>
                                            <h6 className="mb-0">{formatDate(leaveData.generalData.endDate)}</h6>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="p-3 bg-light rounded shadow-sm">
                                            <p className="text-muted mb-1 fs-12">Creation Date</p>
                                            <h6 className="mb-0">{formatDate(leaveData.generalData.creationDate)}</h6>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="p-3 bg-light rounded shadow-sm">
                                            <p className="text-muted mb-1 fs-12">Absence Hours</p>
                                            <h6 className="mb-0 fw-bold">{leaveData.generalData.absenceHours} Hours</h6>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="p-3 bg-light shadow-sm rounded">
                                            <p className="text-muted mb-1 fs-12">Approver</p>
                                            <h6 className="mb-0 text-dark fw-bold">
                                                {leaveData.generalData.approver} {leaveData.generalData.approverId ? `(ID: ${leaveData.generalData.approverId})` : ''}
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Form Values */}
                    <div className="col-12 col-xl-6">
                        <div className="card shadow-sm border-0 h-100 rounded-3">
                            <div className="card-header bg-white border-bottom pt-4 pb-3 px-4 d-flex justify-content-between align-items-center">
                                <h5 className="card-title text-dark fw-bold mb-0">Additional Information</h5>
                                <span className="badge bg-light text-muted border px-2 py-1 fs-11">{additionalFieldsList.length} Fields</span>
                            </div>
                            <div className="card-body px-4 pb-4 pt-4">
                                {additionalFieldsList.length > 0 ? (
                                    <ul className="list-group list-group-flush shadow-sm rounded pb-2 border">
                                        {additionalFieldsList.map((field) => (
                                            <li key={field.key} className="list-group-item d-flex justify-content-between align-items-center py-2.5 px-3">
                                                <span className="text-muted fs-13">{field.label}</span>
                                                <span className="fw-semibold text-end text-dark fs-13 text-break" style={{ maxWidth: '60%' }}>
                                                    {field.value}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="text-center py-4 text-muted">
                                        <i className="ti ti-clipboard-text fs-2 mb-2 d-block opacity-50"></i>
                                        <p className="mb-0 fs-13">No additional fields recorded for this leave request.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Last Note / Remarks Card (Matching Mobile ViewLeave Card title="Last Note") */}
                <div className="card shadow-sm border-0 mb-4 rounded-3">
                    <div className="card-header bg-white border-bottom pt-4 pb-3 px-4 d-flex align-items-center">
                        <i className="ti ti-message-dots fs-20 text-danger me-2"></i>
                        <h5 className="card-title text-dark fw-bold mb-0">Last Note</h5>
                    </div>
                    <div className="card-body px-4 pb-4 pt-4">
                        {remarksList.length > 0 ? (
                            <div className="d-flex flex-column gap-3">
                                {remarksList.map((item: any, idx: number) => (
                                    <div key={idx} className="p-3 bg-light rounded-3 border">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-bold text-dark">{item.Name || 'Approver Note'}</span>
                                            <span className="text-muted fs-12">{formatDate(item.createdAt || item.updatedAt)}</span>
                                        </div>
                                        <p className="mb-0 text-dark fs-13">{item.NoticeText || '-'}</p>
                                    </div>
                                ))}
                            </div>
                        ) : applicantNote ? (
                            <div className="p-3 bg-light rounded-3 border">
                                <span className="text-muted fs-12 d-block mb-1 fw-semibold text-uppercase">Applicant Note</span>
                                <p className="mb-0 text-dark fs-13">{applicantNote}</p>
                            </div>
                        ) : (
                            <div className="text-center py-3 text-muted">
                                <p className="mb-0 fst-italic fs-13">No notes or remarks available for this request.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* attachedFiles Section */}
                <div className="card shadow-sm border-0 my-4 rounded-3">
                    <div className="card-header bg-white border-bottom pt-4 pb-3 px-4 shadow-sm">
                        <h5 className="card-title text-dark fw-bold mb-0"><i className="ti ti-paperclip me-2" style={{ color: '#e12b2b' }}></i>Attached Files</h5>
                    </div>
                    <div className="card-body px-4 pb-4 pt-4">
                        {loadingAttachments ? (
                            <div className="text-center py-4">
                                <span className="spinner-border spinner-border-sm text-danger me-2" role="status"></span>
                                <span className="text-muted fs-13">Loading attachments...</span>
                            </div>
                        ) : Array.isArray(leaveData.attachedFiles) && leaveData.attachedFiles.length > 0 ? (
                            <div className="row g-3 mt-1">
                                {leaveData.attachedFiles.map((file: any, index: number) => {
                                    const fileName = file.fileName || file.FileName || file.name || file.file_name || 'Attachment';
                                    const fileType = file.fileExtension || file.type || file.doc_type || fileName.split('.').pop() || 'FILE';
                                    const fileUrl = file.url || file.Content || file.FileContent || '';
                                    return (
                                        <div key={index} className="col-12 col-md-6 col-lg-4">
                                            <div
                                                className="d-flex align-items-center p-3 border rounded shadow-sm bg-white transition-hover"
                                                style={{ cursor: fileUrl ? 'pointer' : 'default' }}
                                                onClick={() => fileUrl && viewLeaveAttachmentFile(fileUrl)}
                                                title={fileUrl ? 'Click to view/download file' : fileName}
                                            >
                                                <div className="p-2 bg-light rounded text-center me-3">
                                                    <i className={`${getFileIcon(fileType)} fs-3`}></i>
                                                </div>
                                                <div className="flex-grow-1 overflow-hidden">
                                                    <h6 className="mb-0 text-truncate text-dark fw-semibold" title={fileName}>
                                                        {fileName}
                                                    </h6>
                                                    <small className="text-muted text-uppercase">{fileType}</small>
                                                </div>
                                                {fileUrl && (
                                                    <i className="ti ti-external-link text-primary ms-2 fs-16"></i>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-5 bg-light rounded mt-3 border border-dashed">
                                <i className="ti ti-file-off fs-1 text-muted mb-2"></i>
                                <p className="text-muted fw-semibold mb-0">No documents attached with this request</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Back Button */}
                <div className="d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate(routes.LeaveManagement.list, { state: behalf.overrideEmployeeID ? behalf : undefined })}
                    >
                        Back to List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LeaveManagementDetails;

